import { api } from '../api/client';
import { applyI18n, t } from '../i18n';

const SAMPLE_FAILURE_DESCRIPTION = 'Simulated delivery failure (DEBUG)';

export function buildSmsDevSimulateControls(reference: string): HTMLElement {
  const host = document.createElement('div');
  host.className = 'sms-dev-simulate';

  const label = document.createElement('p');
  label.className = 'muted sms-dev-simulate__label';
  label.setAttribute('data-i18n', 'sms.devSimulateLabel');
  label.textContent = t('sms.devSimulateLabel');

  const actions = document.createElement('div');
  actions.className = 'sms-dev-simulate__actions';

  const buttons: Array<{ key: string; code: number; errorDescription?: string }> = [
    { key: 'sms.devSimulateAccepted', code: 0 },
    { key: 'sms.devSimulateDelivered', code: 2 },
    { key: 'sms.devSimulateFailed', code: 3, errorDescription: SAMPLE_FAILURE_DESCRIPTION },
  ];

  for (const entry of buttons) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button';
    button.setAttribute('data-i18n', entry.key);
    button.textContent = t(entry.key);
    button.addEventListener('click', () => {
      void (async () => {
        try {
          await api.simulateSmsStatus({
            reference,
            cmStatusCode: entry.code,
            errorCode: entry.errorDescription ? 'dev_simulated' : null,
            errorDescription: entry.errorDescription ?? null,
          });
        } catch {
          // Auto-progress may have already deleted the row; ignore.
        }
      })();
    });
    actions.append(button);
  }

  host.append(label, actions);
  applyI18n(host);
  return host;
}

let cachedShowDevSimulate: boolean | null = null;

/**
 * Show DEBUG simulate controls in Vite DEV, or when the API reports the SMS log sink.
 * Do not permanently cache failures (e.g. SmsSender-only /api/sms/status 403).
 */
export async function loadUsesDevLogSink(): Promise<boolean> {
  if (cachedShowDevSimulate === true) {
    return true;
  }

  if (import.meta.env.DEV) {
    cachedShowDevSimulate = true;
    return true;
  }

  try {
    const status = await api.getSmsStatus();
    cachedShowDevSimulate = status.usesDevLogSink;
    return cachedShowDevSimulate;
  } catch {
    return false;
  }
}

/** Test helper — clear cached status between tests. */
export function resetUsesDevLogSinkCache(): void {
  cachedShowDevSimulate = null;
}
